import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import Loading from "@/components/Loading";
import NoData from "@/components/NoData";
import SinMatch from "../SinMatch";
import SinMatchLeagues from "../SinMatchLeagues";
import SinMatchHeader from "../SinMatchHeader";
import SinDateTabs from "../SinDateTabs";
import LeagueFilterV20 from "../LeagueFilterV20";
import SinOddsAndBookmaker from "../SinOddsAndBookmaker";
import LoadMoreButton from "../LoadMoreButton";
import { BANG_KEO_PAGE_LIMIT, BANKER, BANKER_EXTENDS } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import {
  CHANGE_BOOKMAKER,
  CHANGE_BOOKMAKER_EXTEND,
} from "@/lib/redux/reducers/actionTypes";
import { buildThemeStyle } from "./buildThemeStyle";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const KbvTiLeV23 = ({ type = "all" }) => {

  const compConfig = useCompConfig();
  const { hasDataConfig } = compConfig;
  const themeStyle = buildThemeStyle(compConfig, hasDataConfig);

  const dispatch = useDispatch();
  const bookmarkerExtend = useSelector(
    (state) => state.bookmarkerExtendReducer,
  );
  const bookmarker = useSelector((state) => state.bookmakerReducer);

  const bookmakerOptions = useMemo(
    () => [
      { value: "-1", label: "Tất cả" },
      ...BANKER.map((item) => ({ value: item.value, label: item.name })),
    ],
    [],
  );

  const bookmakerExtendOptions = useMemo(
    () =>
      BANKER_EXTENDS.map((item) => ({
        value: item.value,
        label: `Tỷ lệ ${item.shortName}`,
      })),
    [],
  );

  const today = dayjs().tz(TZ);
  const todayString = today.format("YYYY-MM-DD");
  const [firstLoad, setFirstLoad] = useState(true);
  const [selectedDate, setSelectedDate] = useState(todayString);
  const [isHotOnly, setIsHotOnly] = useState(false);
  const [selectedLeagueIds, setSelectedLeagueIds] = useState([]);
  const [displayLimit, setDisplayLimit] = useState(20);

  const dateString =
    !selectedDate || selectedDate === ""
      ? "all"
      : selectedDate === "live"
        ? todayString
        : selectedDate;
  const status =
    type === "ketqua" ? "end" : selectedDate === "live" ? "live" : "";

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${dateString}`,
      filterOdds: -1,
      cache: false,
      status,
      page: 1,
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [dateString, status],
  );

  const {
    hotLeaguesWithMatches,
    leaguesWithMatches,
    loading,
    error,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  } = useBangKeoMatches(queryObject, false);

  const normalizedLeaguesWithMatches = useMemo(
    () =>
      (leaguesWithMatches || []).map((item) => {
        const normalizedMatches = Array.isArray(item?.matches)
          ? item.matches
          : Object.values(item?.matches || {});
        return {
          ...item,
          matches: normalizedMatches.filter(Boolean),
        };
      }),
    [leaguesWithMatches],
  );

  const normalizedHotLeaguesWithMatches = useMemo(
    () =>
      (hotLeaguesWithMatches || []).map((item) => {
        const normalizedMatches = Array.isArray(item?.matches)
          ? item.matches
          : Object.values(item?.matches || {});
        return {
          ...item,
          matches: normalizedMatches.filter(Boolean),
        };
      }),
    [hotLeaguesWithMatches],
  );

  const baseLeaguesWithMatches = useMemo(
    () =>
      isHotOnly ? normalizedHotLeaguesWithMatches : normalizedLeaguesWithMatches,
    [isHotOnly, normalizedHotLeaguesWithMatches, normalizedLeaguesWithMatches],
  );

  const filteredLeaguesWithMatches = useMemo(() => {
    if (!selectedLeagueIds?.length) return baseLeaguesWithMatches;
    const idSet = new Set(selectedLeagueIds.map(String));
    return baseLeaguesWithMatches.filter(({ league }) =>
      idSet.has(String(league?.id)),
    );
  }, [baseLeaguesWithMatches, selectedLeagueIds]);

  const allFilteredMatches = useMemo(() => {
    return filteredLeaguesWithMatches.flatMap(({ league, matches }) =>
      (matches || []).map((m) => ({ ...m, league })),
    );
  }, [filteredLeaguesWithMatches]);

  useEffect(() => {
    if (
      !isHotOnly &&
      allFilteredMatches.length < displayLimit + 1 &&
      hasMore &&
      !loading &&
      !isLoadingMore
    ) {
      handleLoadMore();
    }
  }, [
    isHotOnly,
    allFilteredMatches.length,
    displayLimit,
    hasMore,
    loading,
    isLoadingMore,
    handleLoadMore,
  ]);

  const matchIds = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [filteredLeaguesWithMatches],
  );

  const { clearAllData } = useLiveData();
  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => { clearAllData(); };
  }, [clearAllData]);

  useEffect(() => {
    if (
      firstLoad &&
      selectedDate === "live" &&
      !loading &&
      filteredLeaguesWithMatches.length <= 0
    ) {
      setSelectedDate(todayString);
    }
  }, [firstLoad, selectedDate, loading, filteredLeaguesWithMatches, todayString]);

  const handleLeagueFilterChange = (ids) => {
    setSelectedLeagueIds(ids);
    setDisplayLimit(20);
  };

  const handleDateChange = (value) => {
    setIsHotOnly(false);
    setSelectedDate(value);
    setFirstLoad(false);
    setDisplayLimit(20);
  };

  const handleToggleHot = () => {
    setIsHotOnly((prev) => !prev);
    setDisplayLimit(20);
  };

  return (
    <div
      className="kbv-sin-main min-h-screen"
      style={themeStyle}
    >
      <div className="mx-auto max-w-[1240px] px-1 md:px-[12px]">

      <div className="hidden md:flex md:flex-col md:gap-2 mt-4 mb-[8px] max-w-full min-w-0 overflow-visible">
        <div className="w-full">
          <LeagueFilterV20
            selectedIds={selectedLeagueIds}
            onChange={handleLeagueFilterChange}
            className="w-full"
            themeStyle={themeStyle}
          />
        </div>
        <div className="min-w-0 overflow-hidden">
          <SinDateTabs
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            isHotOnly={isHotOnly}
            onToggleHot={handleToggleHot}
          />
        </div>

        <div className="flex w-full min-w-0 justify-end pb-1 md:pb-2">
          <SinOddsAndBookmaker
            extendValue={bookmarkerExtend}
            bookmakerValue={bookmarker}
            extendOptions={bookmakerExtendOptions}
            bookmakerOptions={bookmakerOptions}
            onExtendChange={(val) =>
              dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
            }
            onBookmakerChange={(val) =>
              dispatch({ type: CHANGE_BOOKMAKER, payload: val })
            }
            extendPlaceholder="Tỷ lệ HONGKONG"
            bookmakerPlaceholder="Tất cả"
          />
        </div>
      </div>

      {/* Mobile Controls */}
      <div className="md:hidden flex flex-col gap-2 pt-[8px] mb-[12px]">
        <div className="flex w-full min-w-0 justify-end">
          <SinOddsAndBookmaker
            extendValue={bookmarkerExtend}
            bookmakerValue={bookmarker}
            extendOptions={bookmakerExtendOptions}
            bookmakerOptions={bookmakerOptions}
            onExtendChange={(val) =>
              dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
            }
            onBookmakerChange={(val) =>
              dispatch({ type: CHANGE_BOOKMAKER, payload: val })
            }
            extendPlaceholder="Tỷ lệ HONGKONG"
            bookmakerPlaceholder="Tất cả"
          />
        </div>
        <div className="w-full">
          <LeagueFilterV20
            selectedIds={selectedLeagueIds}
            onChange={handleLeagueFilterChange}
            className="w-full"
            themeStyle={themeStyle}
          />
        </div>
        <div className="w-full">
          <SinDateTabs
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            compact
            isHotOnly={isHotOnly}
            onToggleHot={handleToggleHot}
          />
        </div>
      </div>

      {/* Match List */}
      {loading && <Loading />}
      {!loading && error && <NoData message="Lỗi tải dữ liệu trận đấu" />}
      {!loading && (
        <>
          {filteredLeaguesWithMatches.length === 0 ? (
            <div className="p-10">
              <NoData
                message={
                  isLoadingMore
                    ? "Đang tìm thêm trận đấu phù hợp..."
                    : "Không tìm thấy trận đấu theo tiêu chí lọc"
                }
              />
            </div>
          ) : (
            <div className="kbv-sin-match-table kbv-sin-match-table--borderless flex w-full min-w-0 flex-col gap-y-1">
              <SinMatchHeader />
              <div className="kbv-sin-match-list flex min-w-0 flex-col">
                {filteredLeaguesWithMatches.map(({ league, matches }) => (
                  <SinMatchLeagues
                    key={league?.id || league?.name}
                    league={league}
                    matches={matches}
                    render={(match, idx) => (
                      <SinMatch
                        key={match?.id || idx}
                        match={match}
                        leagueName={league?.nameVN || league?.name}
                        index={idx}
                      />
                    )}
                  />
                ))}
              </div>
            </div>
          )}

          <LoadMoreButton
            isLoading={isLoadingMore}
            onClick={() => setDisplayLimit((prev) => prev + 20)}
            loadingComponent={<Loading />}
            hasMore={allFilteredMatches.length > displayLimit || hasMore}
          />
        </>
      )}
      </div>
    </div>
  );
};

export default KbvTiLeV23;
